<template>
  <div class="channel-setting"
       :class="{ confirming } ">
    <div class="step loading"
         v-if="loading">
      <ui-loading-circle :showLoadingText="true" />
    </div>
    <div v-else-if="confirming"
         class="confirm">
      <div class="confirm-icon">
        <i class="icon icon--dialog-warning"></i>
      </div>
      <div class="confirm-description"
           v-html="confirmOption.description"></div>
      <div class="confirm-buttons">
        <ui-button @click="confirming = false">
          {{ $t('common.no') }}
        </ui-button>
        <ui-button @click="confirmOption.confirm"
                   :primary2="true">
          {{ $t('common.yes') }}
        </ui-button>
      </div>
    </div>
    <div class="step step-1"
         v-else-if="step === 1">
      <div class="channel-header">
        <i class="icon icon--samsung-channel"></i>
        <h3>
          <span v-html="$t('campaignTargeting.channel.name')"></span>
          <ui-icon-tooltip :iconSize="'normal'"
                           arrowBottom
                           arrowCenter>
            <span v-html="$t('campaignTargeting.channel.tooltip')"></span>
          </ui-icon-tooltip>
        </h3>
        <p v-html="$t('campaignTargeting.channel.description')"></p>
      </div>
      <div class="channel-body">
        <div v-if="hasData"
             class="channel-selection">
          <div class="channel-body"
               v-if="listItems"
               :key="resetKey">
            <ui-check-selectbox v-if="listItems"
                                :width="'100%'"
                                :height="'315px'"
                                :full="true"
                                :maxHeight="315"
                                :value="selectedChannel"
                                :items="items"
                                :showCount="true"
                                :unselectLastOne="true"
                                @input="onSelectedChannelChange"/>
          </div>
        </div>
        <div v-else
             class="channel-empty">
          <i class="icon icon--empty"></i><br>
          {{ $t('common.noCollectedData') }}
        </div>
      </div>
      <div class="channel-footer">
        <ui-button class="back"
                   :iconName="'icon--arrow-left-black'"
                   @click="onCloseLayer(false)">{{ $t('common.back') }}</ui-button>
        <ui-button @click="resetSelected"
                   :iconName="'icon--reset'"
                   :disabled="!selectedChannel || selectedChannel.length < 1">{{ $t('common.reset') }}</ui-button>
        <ui-button @click="applySelected"
                   :disabled="JSON.stringify(selectedChannel) === JSON.stringify(channelCache)"
                   :primary2="true">{{ $t('common.apply') }}</ui-button>
      </div>
    </div>
  </div>
</template>

<script>
import { getChannel } from '@/api/report'
import UiButton from '@/components/ui/UiButton'
import UiCheckSelectbox from '@/components/ui/UiCheckSelectbox'
import UiIconTooltip from '@/components/ui/UiIconTooltip'
import UiLoadingCircle from '@/components/ui/UiLoadingCircle'

export default {
  name: 'ChannelSetting',
  components: {
    UiButton,
    UiCheckSelectbox,
    UiIconTooltip,
    UiLoadingCircle
  },
  props: ['aid', 'startDate', 'endDate', 'channelCache', 'otherKeySet'],
  methods: {
    setChannelData (result) {
      let listItems = {}
      let hasItems = false
      for (const [key, value] of Object.entries(result)) {
        listItems[key] = value
        hasItems = true
      }

      const category = this.$t('campaignTargeting.channel.list')
      if (hasItems) {
        this.listItems = { }
        this.listItems[category] = listItems
      } else {
        this.listItems = false
      }
      this.loading = false
    },
    onSelectedChannelChange (value) {
      this.selectedChannel = value
    },
    resetSelected () {
      this.selectedChannel = []
      this.resetKey = new Date().toString()
    },
    applySelected () {
      this.onCloseLayer(false, false)
    },
    onCloseLayer (reset, close) {
      let selectedChannel = this.selectedChannel
      if (!selectedChannel || selectedChannel.length < 1) {
        selectedChannel = null
      }
      if (reset) {
        if (JSON.stringify(selectedChannel) !== JSON.stringify(this.initialChannel)) {
          this.showConfirm(this.$t('dialog.campaignTargeting.noAppliedAndClose.desc'), () => { this.$emit('onCloseLayer') })
        } else {
          this.$emit('onSetChannel', selectedChannel)
          if (close) {
            this.$emit('onCloseLayer')
          }
        }
      } else {
        this.$emit('onSetChannel', selectedChannel)
        if (close) {
          this.$emit('onCloseLayer')
        }
      }
    },
    showConfirm (description, confirm) {
      this.confirmOption.description = description
      this.confirmOption.confirm = confirm
      this.confirming = true
    }
  },
  created () {
    this.initialChannel = this.channelCache
    this.selectedCid = this.initialChannel ? [...this.initialChannel] : []

    this.loading = true
    this.step = 1
    getChannel({
      startDate: this.startDate,
      endDate: this.endDate,
      aid: this.aid
    })
      .then(result => {
        result = {
          paidSearch: 65.3,
          naturalSearch: 19.6,
          display: 32.1,
          socialMediaCampaign: 34.7,
          push: 32.6,
          mobileApplication: 9.4,
          affiliateMarketing: 6.7,
          email: 4.3,
          qrCode: 12.6,
          otherLinks: 7.3,
          textLink: 8.2,
          otherCampaigns: 4.2,
          socialNetworkReferral: 12.3,
          sessionRefresh: 31.2,
          direct: 0.3,
          other: 0.1
        }

        this.channelData = result
        this.setChannelData(result)
        this.loading = false
      })
  },
  computed: {
    hasData () {
      if (!this.channelData) {
        return false
      }
      let has = false
      if (typeof this.channelData === 'object') {
        Object.keys(this.channelData).forEach(key => {
          has = true
        })
      }
      return has
    },
    items () {
      if (!this.listItems) {
        return false
      }

      let list = Object.entries(this.listItems)
        .map(([key, value]) => {
          return {
            value: key,
            text: key,
            items: Object.entries(value).map(([k, v]) => {
              return {
                value: k,
                text: this.$t('campaignTargeting.channel.' + k),
                desc: v + '%'
              }
            })
          }
        })

      return list
    }
  },
  data () {
    return {
      step: 1,
      loading: false,
      initialChannel: null,
      listItems: false,
      selectedChannel: [],
      confirming: false,
      confirmOption: {
        description: '',
        confirm: () => {}
      },
      channelData: null,
      resetKey: new Date().toString()
    }
  }
}
</script>

<style lang="scss" scoped>
.channel-setting {
  width: 731px;
  height: 526px;
  border-radius: 14px;
  box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
  background-color: #ffffff;

  &.confirming {
    position: relative;
    width: 360px;
    height: 270px;

    .confirm {
      opacity: 1;
      z-index: 10;
    }

    .step {
      opacity: 0;
      z-index: 1;
    }
  }

  .confirm {
    position: relative;
    text-align: center;
    opacity: 0;

    .confirm-icon {
      padding-top: 40px;
    }

    .confirm-description {
      font-size: 14px;
      color: #444444;
      line-height: 1.43;
      margin-top: 13px;
      padding: 0 38px;
      height: 100px;
    }

    .confirm-buttons {
      .ui-btn {
        margin: 0 2px 0 3px;
        width: 86px;
      }
    }
  }

  .step {
    position: relative;
    background-color: #ffffff;
    border-radius: 15px;
    width: 100%;
    height: 100%;

    &.loading {
      z-index: 100;
    }

    &.step-1 {
      .channel-header {
        position: relative;
        margin: 0 30px;

        .icon {
          @include positionYcenter;
          top: calc(50% + 7px);
          border-right: 1px solid #d5d5d5;
          background-position: center left;
          height: 36px;
          padding-right: 20px;
        }

        h3 {
          font-size: 18px;
          color: #444444;
          line-height: 1.17;
          padding: 36px 0 0 144px;

          .tooltip {
            top: 2px;
            left: 6px;

            & /deep/ {
              .icon {
                width: 15px;
                height: 15px;
                background-size: 100% 100%;
              }

              .bubble {
                margin-bottom: 3px !important;
                margin-left: -10.5px !important;
              }
            }
          }
        }

        p {
          font-size: 13px;
          color: #444444;
          line-height: 1.38;
          padding: 6px 0 23px 145px;

          & /deep/ span {
            color: #ec0047;
          }
        }
      }

      .channel-body {
        white-space: nowrap;
        padding: 8px 15px 14px 15px;

        .empty {
          width: 100%;
          height: 312px;
          border-radius: 5px;
          border: 1px solid #d7d7d7;
          background-color: #f4f4f4;
          line-height: 1.77;
          color: #666666;
          font-size: 14px;
          text-align: center;
          margin-bottom: 3px;

          i {
            margin-top: 104px;
          }
        }

        .channel-empty {
          height: 127px;
          text-align: center;
          line-height: 1.46;
          font-size: 14px;
          color: #555555;

          i {
            margin-top: 3px;
            margin-bottom: 3px;
          }
        }
      }

      .channel-footer {
        margin: 0 15px 20px;
        text-align: center;
        position: relative;

        .ui-btn {
          margin: 0 2px 0 3px;
          width: 95px;

          &.back {
            position: absolute;
            top: 0;
            left: 0;
            border: none;
            font-size: 13px;
            width: 75px;
            text-align: center;
          }
        }
      }
    }
  }
}
</style>
